import { Collapsible, InputElement } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  IQuoteItemRespSchema,
  IQuoteOfferingRespSchema,
  OnQuoteOfferingChange,
  PriceUpliftConfigFormInputType,
  PriceUpliftConfigFormSchema,
  PriceUpliftConfigFormType,
  PriceUpliftConfigTypeEnum,
  PriceUpliftPercentageTypeEnum,
  PriceUpliftProductConfigurationType,
} from '@monetize/types/app';
import { sortByProductPosition } from '@monetize/utils/core';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { MdInfo } from 'react-icons/md';
import { handleApiErrorToast } from '../../api/axios';
import {
  useCreatePriceUpliftConfig,
  useDeletePriceUpliftConfig,
  useGetPriceUpliftConfig,
  useUpdatePriceUpliftConfig,
} from '../../api/priceUpliftService';
import { ONE_TIME_PRODUCT_TYPES } from '../../constants/products';
import { logger } from '../../services/logger';
import {
  MBox,
  MButton,
  MCheckbox,
  MCustomNumberInput,
  MDivider,
  MFlex,
  MFormField,
  MGrid,
  MGridItem,
  MIcon,
  MRadio,
  MRadioGroup,
  MSkeleton,
  MStack,
  MSwitch,
  MText,
  MTooltip,
} from '../Monetize';

type PriceUpliftConfigurationProps = {
  isReadOnly: boolean;
  quoteOffering: IQuoteOfferingRespSchema;
  onOfferingChange: OnQuoteOfferingChange;
};

const fixOrder = (
  data: PriceUpliftProductConfigurationType[],
  order: IQuoteItemRespSchema[],
): PriceUpliftProductConfigurationType[] => {
  const orderMap = new Map<string, number>();
  for (let i = 0; i < order.length; i++) {
    orderMap.set(order[i].productId, i);
  }
  return data.sort((a, b) => {
    const aIndex = orderMap.get(a.productId) ?? 0;
    const bIndex = orderMap.get(b.productId) ?? 0;
    return aIndex - bIndex;
  });
};

export const PriceUpliftConfiguration = ({
  isReadOnly,
  quoteOffering,
  onOfferingChange,
}: PriceUpliftConfigurationProps) => {
  const items = sortByProductPosition(quoteOffering.items).filter(
    ({ productType }) => !ONE_TIME_PRODUCT_TYPES.has(productType),
  );
  const canUpdateAndDelete = !!quoteOffering.priceUpliftConfigurationId;
  const [canPriceUplift, setCanPriceUplift] = useState<boolean>(
    !!quoteOffering.priceUpliftConfigurationId,
  );

  const { isLoading: isPriceUpliftConfigLoading, data: priceUpliftConfig } =
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    useGetPriceUpliftConfig(quoteOffering.priceUpliftConfigurationId!, {
      enabled: !!quoteOffering.priceUpliftConfigurationId,
      refetchOnWindowFocus: false,
    });

  const isLocked = priceUpliftConfig?.locked ?? false;

  /** calculate values from priceUpliftConfig service */
  let values;

  if (priceUpliftConfig) {
    values = {
      amount: priceUpliftConfig.amount,
      type: priceUpliftConfig.type,
      percentageType:
        priceUpliftConfig.percentageType ?? PriceUpliftPercentageTypeEnum.FIXED,
      productConfigurations: priceUpliftConfig.productConfigurations
        ? fixOrder(priceUpliftConfig.productConfigurations, items)
        : items.map(({ productId }) => ({
            productId,
            percentageType: priceUpliftConfig.percentageType,
            amount: null,
          })),
    };
  }

  const {
    watch,
    control,
    formState: { isValid, isDirty },
    handleSubmit,
    setValue,
  } = useForm<
    PriceUpliftConfigFormInputType,
    unknown,
    PriceUpliftConfigFormType
  >({
    mode: 'onChange',
    resolver: zodResolver(PriceUpliftConfigFormSchema),
    values,
    // `values` only reaches the form after the first render, so a cached
    // configuration is also the starting point; otherwise a product-specific
    // one would briefly show as apply-to-all.
    defaultValues: values ?? {
      amount: null,
      percentageType: PriceUpliftPercentageTypeEnum.FIXED,
      type: PriceUpliftConfigTypeEnum.APPLY_TO_ALL_PRODUCTS,
      productConfigurations: items.map(({ productId }) => ({
        amount: null,
        productId,
        percentageType: PriceUpliftPercentageTypeEnum.FIXED,
      })),
    },
  });

  // A configuration that loads after the first render reaches the form from
  // an effect, a render late. The form stays behind the skeleton until then,
  // rather than showing as apply-to-all and jumping open. This effect runs
  // after the form's own, so both land in the same update.
  const [isConfigInForm, setIsConfigInForm] = useState(!!priceUpliftConfig);
  useEffect(() => {
    if (priceUpliftConfig) {
      setIsConfigInForm(true);
    }
  }, [priceUpliftConfig]);

  const { mutateAsync: doCreatePriceUpliftConfig, isPending: isCreateLoading } =
    useCreatePriceUpliftConfig({
      onSuccess: (data) => {
        onOfferingChange(
          'UPDATE',
          {
            ...quoteOffering,
            priceUpliftConfigurationId: data.id,
          } as IQuoteOfferingRespSchema,
          undefined,
          false,
          true,
        );
      },
      onError: (err) => {
        handleApiErrorToast(err);
      },
    });
  const { mutateAsync: doUpdatePriceUpliftConfig, isPending: isUpdateLoading } =
    useUpdatePriceUpliftConfig({
      onSuccess: () => {
        onOfferingChange('UPDATE', quoteOffering, undefined, false, true);
      },
      onError: (err) => {
        handleApiErrorToast(err);
      },
    });

  const { mutate: doDeletePriceUpliftConfig, isPending: isDeleteLoading } =
    useDeletePriceUpliftConfig({
      meta: { showErrorToast: true },
      onSuccess: () => {
        onOfferingChange(
          'UPDATE',
          {
            ...quoteOffering,
            priceUpliftConfigurationId: null,
          } as IQuoteOfferingRespSchema,
          undefined,
          false,
          true,
        );
      },
    });

  const handlePriceUpliftToggle = (isEnabled: boolean) => {
    setCanPriceUplift(isEnabled);
    if (
      !isEnabled &&
      canUpdateAndDelete &&
      quoteOffering.priceUpliftConfigurationId
    ) {
      doDeletePriceUpliftConfig({
        quoteId: quoteOffering.quoteId,
        quoteOfferingId: quoteOffering.id,
        priceUpliftConfigurationId: quoteOffering.priceUpliftConfigurationId,
      });
    }
  };

  const onSubmit = async (formData: PriceUpliftConfigFormType) => {
    const payload: PriceUpliftConfigFormType = {
      ...formData,
      productConfigurations:
        formData.type === PriceUpliftConfigTypeEnum.APPLY_TO_ALL_PRODUCTS
          ? null
          : (formData.productConfigurations ?? [])
              .filter(({ amount }) => Boolean(amount))
              .map((config) => ({
                ...config,
                percentageType: formData.percentageType,
              })),
    };

    // Clearing every product means no uplift: delete it like the toggle does,
    // since an empty configuration still counts as an uplift in rules. The
    // switch only turns off once the delete succeeds, so a failed one leaves
    // the form open to retry.
    if (
      payload.type === PriceUpliftConfigTypeEnum.PRODUCT_SPECIFIC &&
      !payload.productConfigurations?.length
    ) {
      if (quoteOffering.priceUpliftConfigurationId) {
        doDeletePriceUpliftConfig(
          {
            quoteId: quoteOffering.quoteId,
            quoteOfferingId: quoteOffering.id,
            priceUpliftConfigurationId:
              quoteOffering.priceUpliftConfigurationId,
          },
          { onSuccess: () => setCanPriceUplift(false) },
        );
      } else {
        setCanPriceUplift(false);
      }
      return;
    }

    const parsed = PriceUpliftConfigFormSchema.safeParse(payload);
    if (parsed.success) {
      const commonParams = {
        quoteOfferingId: quoteOffering.id ?? '',
        priceUpliftConfig: parsed.data,
      };

      if (!canUpdateAndDelete) {
        await doCreatePriceUpliftConfig({
          quoteId: quoteOffering.quoteId ?? '',
          ...commonParams,
        });
      } else {
        if (quoteOffering.priceUpliftConfigurationId) {
          await doUpdatePriceUpliftConfig({
            quoteId: quoteOffering.quoteId ?? '',
            priceUpliftConfigurationId:
              quoteOffering.priceUpliftConfigurationId,
            ...commonParams,
          });
        } else {
          logger.error(
            'priceUpliftConfigurationId is not available for updating.',
          );
        }
      }
    } else {
      logger.error('Unable to parse data', parsed.error);
    }
  };

  const onError = (err: any) => logger.error(err);

  const percentageType = watch('percentageType');
  // Follows the form's type, which a configuration that loads after the first
  // render sets once its request resolves.
  const isApplyToAll =
    watch('type') === PriceUpliftConfigTypeEnum.APPLY_TO_ALL_PRODUCTS;
  const isLockedOrIsReadOnly = isLocked || isReadOnly;

  if (isPriceUpliftConfigLoading || (priceUpliftConfig && !isConfigInForm)) {
    return (
      <MFlex direction="column">
        <MSkeleton h={40} w="full" />
      </MFlex>
    );
  }

  return (
    <MFlex flexDir="column" gap={4}>
      <MFlex justify="space-between" w="full">
        <MText fontWeight="bold" fontSize="md">
          Price Uplift on Renewal
        </MText>
        <MSwitch
          aria-label="Price uplift on renewal"
          isDisabled={isLockedOrIsReadOnly || isDeleteLoading}
          isReadOnly={isLockedOrIsReadOnly}
          isChecked={canPriceUplift}
          onChange={(ev) => handlePriceUpliftToggle(ev.target.checked)}
        />
      </MFlex>

      {canPriceUplift && (
        <MFlex
          flexDirection="column"
          gap={4}
          as="form"
          id="price-uplift-config-form"
          onSubmit={handleSubmit(onSubmit, onError)}
        >
          <MText>
            Price increase can be automatically added to the renewal Quote. Set
            a percentage or a max limit for uplift pricing
          </MText>
          <MFormField>
            <Controller
              control={control}
              name="percentageType"
              render={({ field: { value, onChange, ...rest } }) => (
                <MRadioGroup
                  isDisabled={isLockedOrIsReadOnly || isDeleteLoading}
                  onChange={onChange}
                  value={value}
                  {...rest}
                >
                  <MStack gap={4} direction="row" color="tGray.darkPurple">
                    <MRadio value={PriceUpliftPercentageTypeEnum.FIXED}>
                      Fixed Percentage
                    </MRadio>
                    <MRadio value={PriceUpliftPercentageTypeEnum.MAX}>
                      <MText display="inline-flex" alignItems="center">
                        Max Percentage
                        <MTooltip
                          label="Maximum percentage allowed for the price to increase"
                          placement="bottom-start"
                          shouldWrapChildren
                        >
                          <MIcon
                            display="flex"
                            alignItems="center"
                            as={MdInfo}
                            color="tPurple.base"
                            mx={1}
                          />
                        </MTooltip>
                      </MText>
                    </MRadio>
                  </MStack>
                </MRadioGroup>
              )}
            />
          </MFormField>

          <MFlex w="full" gap={4} align="center">
            <MFormField w="20%">
              <Controller
                control={control}
                name="amount"
                render={({ field: { value, onChange, ...rest } }) => (
                  <MBox position="relative">
                    <MCustomNumberInput
                      isReadOnly={isLockedOrIsReadOnly}
                      inputMode="decimal"
                      value={value ?? ''}
                      onChange={(valueAsString) => {
                        onChange(valueAsString);
                      }}
                      placeholder="0"
                      isDisabled={!isApplyToAll || isDeleteLoading}
                      {...rest}
                    />
                    {/* v2's InputGroup sized this as a 30px square at the input's
                        edge; v3's InputElement only gets a `top` inside a group. */}
                    <InputElement
                      placement="end"
                      top="0"
                      w="30px"
                      px="0"
                      fontWeight="bold"
                    >
                      %
                    </InputElement>
                  </MBox>
                )}
              />
            </MFormField>
            <MFormField w="80%">
              <Controller
                name="type"
                control={control}
                render={({ field: { value, onChange, ...rest } }) => (
                  <MCheckbox
                    isReadOnly={isLockedOrIsReadOnly}
                    isDisabled={isDeleteLoading}
                    value={value}
                    isChecked={isApplyToAll}
                    onChange={(ev) => {
                      setValue(
                        'type',
                        ev.target.checked
                          ? PriceUpliftConfigTypeEnum.APPLY_TO_ALL_PRODUCTS
                          : PriceUpliftConfigTypeEnum.PRODUCT_SPECIFIC,
                      );
                    }}
                    {...rest}
                  >
                    Apply to all products
                  </MCheckbox>
                )}
              />
            </MFormField>
          </MFlex>
          <MBox>
            <Collapsible.Root open={!isApplyToAll}>
              <Collapsible.Content>
                <MDivider />
                <MFlex gap={4} flexDirection="column" my={4}>
                  {items &&
                    items.map(({ id, productName, productId }, idx) => (
                      <MGrid key={id} templateColumns="3fr 1fr" columnGap={4}>
                        <MGridItem>
                          <MTooltip label={productName}>
                            <MText lineClamp={1}>{productName}</MText>
                          </MTooltip>
                        </MGridItem>
                        <MGridItem>
                          <MFormField>
                            <Controller
                              name={`productConfigurations.${idx}.amount`}
                              control={control}
                              render={({
                                field: { value, onChange, ...rest },
                              }) => (
                                <MBox position="relative">
                                  <MCustomNumberInput
                                    isReadOnly={isLocked}
                                    isDisabled={isDeleteLoading}
                                    inputMode="decimal"
                                    placeholder="0"
                                    value={value || ''}
                                    onChange={(valueAsString) => {
                                      onChange(valueAsString);
                                      setValue(
                                        `productConfigurations.${idx}.productId`,
                                        productId,
                                      );
                                      setValue(
                                        `productConfigurations.${idx}.percentageType`,
                                        percentageType,
                                      );
                                    }}
                                    {...rest}
                                  />
                                  <InputElement
                                    placement="end"
                                    top="0"
                                    w="30px"
                                    px="0"
                                    fontWeight="bold"
                                  >
                                    %
                                  </InputElement>
                                </MBox>
                              )}
                            />
                          </MFormField>
                        </MGridItem>
                      </MGrid>
                    ))}
                </MFlex>
              </Collapsible.Content>
            </Collapsible.Root>
            <MDivider />
          </MBox>
          <MFlex justify="flex-end" align="center">
            <MButton
              form="price-uplift-config-form"
              type="submit"
              variant="primary"
              isDisabled={
                !isValid ||
                !isDirty ||
                isLockedOrIsReadOnly ||
                isCreateLoading ||
                isUpdateLoading ||
                isDeleteLoading
              }
              isLoading={isCreateLoading || isUpdateLoading || isDeleteLoading}
            >
              Save
            </MButton>
          </MFlex>
        </MFlex>
      )}
    </MFlex>
  );
};
